import { NavigateFunction } from 'react-router-dom';
import { AuthLib } from '../contexts/AuthContext/AuthLib';

/**
 * Build the full url for an api interaction. Typically not called directly, and
 *  the BuildUrl in the AbstractApi base class is used.
 * @param path The path to build with
 * @returns The structured path to interact with the api
 */
export const buildApiUrl = (path: string) => `/api/${path}`;

/**
 * The properties for the abstract api
 */
export interface ApiProps {
  /**
   * The auth library
   */
  authLib: AuthLib;

  /**
   * React router navigate
   */
  navigate: NavigateFunction;
}

/**
 * The base class for api interactions
 */
export abstract class AbstractApi {
  private _authLib: AuthLib;
  private _navigate: NavigateFunction;

  constructor(apiProps: ApiProps) {
    this._authLib = apiProps.authLib;
    this._navigate = apiProps.navigate;
  }

  /**
   * Build the full url for an api interaction
   * @param path The path to build with
   * @returns The structured path to interact with the api
   */
  protected BuildUrl(path: string): string {
    return buildApiUrl(path);
  }

  /**
   * Make an authenticated request. This will inject the current token with the request.
   * @param input The request info
   * @param init The request init
   * @returns The result of the fetch call
   */
  protected async authFetch(
    input: RequestInfo,
    init: RequestInit
  ): Promise<Response> {
    const setAuthHeader = (token: string | null | undefined) => {
      const reqHeaders = new Headers(init.headers);
      reqHeaders.set('Authorization', `Bearer ${token}`);
      init.headers = reqHeaders;
    };

    // Setup authentication headers
    if (!init) {
      init = {};
    }
    if (!init.headers) {
      init.headers = {};
    }
    setAuthHeader(this._authLib.GetToken());

    // Make the request
    let response = await fetch(input, init);
    if (response.status == 401) {
      const refreshedToken = await this._authLib.RefreshToken();
      if (refreshedToken) {
        setAuthHeader(refreshedToken);
        response = await fetch(input, init);
      }
    }

    if (response.status == 401) {
      this._authLib.Logout();
      this._navigate('/login');
      throw Error('Unauthorized');
    }
    if (
      response.status == 403 &&
      (await response.text()) == 'active-license-required'
    ) {
      this._navigate('/inactive-license');
      throw Error('Active license required for the organization');
    }

    return response;
  }
}
